import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Moon, Sun } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Sidebar } from "@/components/Sidebar";
import { PreviewToolbar } from "@/components/PreviewToolbar";
import { SendDialog } from "@/components/SendDialog";
import { BulkDialog } from "@/components/BulkDialog";
import { HistoryView } from "@/components/HistoryView";
import { CodeDialog } from "@/components/CodeDialog";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGOS = {
  light: "/logo-light.png",
  dark: "/logo-dark.png",
};

export default function App() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("light");
  const [device, setDevice] = useState("desktop");
  const [rendered, setRendered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [customHtml, setCustomHtml] = useState(null);
  const [view, setView] = useState("studio");
  const iframeRef = useRef(null);

  const applyEditable = useCallback((edit) => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.body) {
      doc.body.contentEditable = edit ? "true" : "false";
      doc.body.style.outline = "none";
    }
  }, []);

  useEffect(() => {
    applyEditable(editing);
  }, [editing, applyEditable]);

  const getCurrentHtml = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.documentElement) {
      const clone = doc.documentElement.cloneNode(true);
      const body = clone.querySelector("body");
      if (body) {
        body.removeAttribute("contenteditable");
        body.style.outline = "";
      }
      return "<!DOCTYPE html>\n" + clone.outerHTML;
    }
    return rendered?.html || "";
  }, [rendered]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    axios.get(`${API}/templates`).then((res) => {
      setTemplates(res.data.templates);
      if (res.data.templates.length) setSelectedId(res.data.templates[0].id);
    }).catch(() => toast.error("Could not load templates"));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    setCustomHtml(null);
    axios.get(`${API}/templates/${selectedId}`, { params: { mode, content: "sample" } })
      .then((res) => setRendered(res.data))
      .catch(() => toast.error("Could not render template"))
      .finally(() => setLoading(false));
  }, [selectedId, mode]);

  const onCopy = useCallback(async () => {
    if (!rendered) return;
    try {
      await navigator.clipboard.writeText(getCurrentHtml());
      toast.success("HTML copied to clipboard");
    } catch {
      toast.error("Clipboard blocked by browser — use Download instead");
    }
  }, [rendered, getCurrentHtml]);

  const onDownload = useCallback(() => {
    if (!rendered) return;
    const blob = new Blob([getCurrentHtml()], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `nexxy-${rendered.id}-${mode}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Template downloaded");
  }, [rendered, mode]);

  return (
    <div className="App h-screen flex flex-col bg-background text-foreground">
      <header data-testid="app-header" className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3 overflow-hidden">
          <motion.img
            initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            src={LOGOS[mode]} alt="NEXXY" className="h-7 w-auto" data-testid="header-logo" />
          <motion.span
            initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif-display italic text-lg text-accent">Email Studio</motion.span>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full border border-border p-0.5 bg-secondary/50">
            <button data-testid="view-studio-btn" onClick={() => setView("studio")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === "studio" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Studio
            </button>
            <button data-testid="view-history-btn" onClick={() => setView("history")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${view === "history" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              History
            </button>
          </div>
          <button
            data-testid="theme-toggle-btn"
            onClick={() => setMode(mode === "light" ? "dark" : "light")}
            className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {mode === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {view === "history" ? (
          <HistoryView templates={templates} />
        ) : (
        <>
        <Sidebar templates={templates} selectedId={selectedId} onSelect={setSelectedId} />

        <main className="flex-1 flex flex-col min-w-0">
          {rendered && (
            <div data-testid="subject-bar" className="px-6 py-3 border-b border-border">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-1">Subject line</p>
              <p data-testid="subject-text" className="font-serif-display text-xl">{rendered.subject}</p>
              {rendered.placeholders.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {rendered.placeholders.map((p) => (
                    <span key={p} data-testid={`placeholder-chip-${p}`}
                      className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                      {`{{${p}}}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <PreviewToolbar
            device={device} setDevice={setDevice}
            editing={editing} setEditing={setEditing}
            onCode={() => setCodeOpen(true)}
            onDownload={onDownload}
            onSend={() => setSendOpen(true)}
            onBulk={() => setBulkOpen(true)}
            bulkAllowed={templates.find((t) => t.id === selectedId)?.bulk}
          />

          <div data-testid="preview-area" className="flex-1 overflow-auto bg-secondary/40 flex justify-center p-6">
            {loading && !rendered ? (
              <p className="text-sm text-muted-foreground mt-10">Loading preview…</p>
            ) : rendered ? (
              <iframe
                ref={iframeRef}
                data-testid="email-preview-iframe"
                title="Email preview"
                className={`preview-frame fade-up rounded-xl shadow-lg bg-white ${editing ? "ring-2 ring-accent" : ""}`}
                style={{ width: device === "mobile" ? 390 : 680, height: "100%", minHeight: 600 }}
                srcDoc={customHtml || rendered.html}
                onLoad={() => applyEditable(editing)}
              />
            ) : null}
          </div>
        </main>
        </>
        )}
      </div>
      <SendDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        rendered={rendered}
        sender={templates.find((t) => t.id === selectedId)?.sender}
        getHtml={getCurrentHtml}
      />
      <BulkDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        rendered={rendered}
        sender={templates.find((t) => t.id === selectedId)?.sender}
        mode={mode}
        customHtml={customHtml}
        onSent={() => setView("history")}
      />
      <CodeDialog
        open={codeOpen}
        onOpenChange={setCodeOpen}
        getHtml={getCurrentHtml}
        onSave={setCustomHtml}
      />
      <Toaster position="bottom-right" />
    </div>
  );
}
